import React, { useState } from 'react';
import * as mobxReact from 'mobx-react';
import Popover, { PopoverInfo } from 'react-tiny-popover';
import classnames from 'classnames';
import './attendee-column-popover.scss';
import { IAttendeeDetailsDTO, MeetingAttendeeIssue } from '../../../../api/attendees.dao';
import { HEADER_HEIGHT } from '../../../../constants/common.constants';
import PopoverContent from './attendee-column-popover_content';
import { ATTENDEE_INFO_POPOVER_TRACKING_EVENTS, OpenFrom } from '../../../../tracking-service/tracking-values';
import { AttendeeColumnPopoverViewmodel } from './attendee-column-popover.viewmodel';
import { IAttendeeAdditionalInfo } from '../../../_events/_manage-event/shared/attendees-section/_attendee/_attendee';
import { Permission } from '../../../../api/roles.dao';

interface IBaseProps {
    padding?: number;
    children: React.ReactNode;
}

interface IFetchModeProps extends IBaseProps {
    attendeeId: number;
    issues: MeetingAttendeeIssue[];
    vm: AttendeeColumnPopoverViewmodel;
    withLiveStatus?: boolean;
    withRemove?: boolean;
    organizationId?: number;
    meetingAttendeeId?: string;
    isOpen?: boolean;
    openFrom?: OpenFrom;
    onAttendeeEditSaveCallback?: (attendee: IAttendeeDetailsDTO) => void;
    handleRemoveAttendeeClick?: (attendeeId: number, organizationId: number) => void;
}

interface ILocalDataModeProps extends IBaseProps {
    attendeeInfo: IAttendeeAdditionalInfo;
    onEdit: () => void;
    editPermission?: Permission;
}

type IProps = IFetchModeProps | ILocalDataModeProps;

function isLocalDataMode(props: IProps): props is ILocalDataModeProps {
    return 'attendeeInfo' in props && 'onEdit' in props;
}

const AttendeeDetailsPopover = mobxReact.observer((props: IProps) => {
    const padding = props.padding ?? 10;

    if (isLocalDataMode(props)) {
        return <AttendeeDetailsPopoverLocalData {...props} padding={padding} />;
    }
    return <AttendeeDetailsPopoverFetchMode {...props} padding={padding} />;
});

const AttendeeDetailsPopoverLocalData = (props: ILocalDataModeProps & { padding: number }) => {
    const { attendeeInfo, onEdit, editPermission, children, padding } = props;
    const [isOpen, setIsOpen] = useState(false);

    const handleEdit = () => {
        setIsOpen(false);
        onEdit();
    };

    return (
        <Popover
            isOpen={isOpen}
            position={['bottom', 'top']}
            align='start'
            padding={padding}
            containerClassName={'fixed-position react-tiny-popover-container attendee-column-popover'}
            contentLocation={(args: PopoverInfo) => ({
                top: args.nudgedTop < HEADER_HEIGHT ? HEADER_HEIGHT : args.nudgedTop,
                left: args.nudgedLeft,
            })}
            onClickOutside={() => setIsOpen(false)}
            content={isOpen && (
                <PopoverContent
                    issues={[]}
                    notes={[]}
                    attendeeInfo={attendeeInfo}
                    attendeeId={0}
                    openAttendeePopover={handleEdit}
                    handleAttendeeRemoval={() => { return; }}
                    editPermission={editPermission}
                />
            )}
        >
            <div
                className={classnames('attendee-details-popover', { 'attendee-details-popover__active': isOpen })}
                onClick={() => setIsOpen(!isOpen)}
            >
                {children}
            </div>
        </Popover>
    );
};

const AttendeeDetailsPopoverFetchMode = mobxReact.observer((props: IFetchModeProps & { padding: number }) => {
    const {
        issues,
        children,
        padding,
        attendeeId,
        withLiveStatus = false,
        withRemove = false,
        vm,
        organizationId,
        isOpen,
        meetingAttendeeId,
        openFrom,
        onAttendeeEditSaveCallback,
        handleRemoveAttendeeClick,
    } = props;

    const handleAttendeeRemoval = () => {
        handleRemoveAttendeeClick(attendeeId, organizationId);
        vm.setClose();
    };

    return (
        <Popover
            isOpen={isOpen}
            position={['bottom', 'top']}
            align='start'
            padding={padding}
            containerClassName={'fixed-position react-tiny-popover-container attendee-column-popover'}
            contentLocation={(args: PopoverInfo) => {
                return {
                    top: args.nudgedTop < HEADER_HEIGHT ? HEADER_HEIGHT : args.nudgedTop,
                    left: args.nudgedLeft,
                };
            }}
            onClickOutside={() => vm.setClose()}
            content={
                (!vm.loading && isOpen) && <PopoverContent
                    issues={issues}
                    notes={vm.notes}
                    attendeeInfo={vm.attendeeInfo}
                    attendeeId={attendeeId}
                    openAttendeePopover={() => vm.openAttendeePopover(attendeeId, openFrom, onAttendeeEditSaveCallback)}
                    withIssues={withLiveStatus}
                    withNotes={withLiveStatus}
                    handleAttendeeRemoval={handleAttendeeRemoval}
                    withRemove={withRemove}
                    handleAttendeeCopy={() => vm.trackEvent(ATTENDEE_INFO_POPOVER_TRACKING_EVENTS.OPENED_FROM, openFrom)}
                />}
        >
            <div
                className={classnames('attendee-details-popover', {
                    'attendee-details-popover__with-walkIn-icon': withLiveStatus
                }, { 'attendee-details-popover__active': isOpen })}
                onClick={() => vm.handleOpenPopover(attendeeId, meetingAttendeeId, openFrom)}
            >
                {children}
            </div>
        </Popover>
    );
});

export { AttendeeDetailsPopover };
